import React, { useMemo } from 'react';
import { cohortConfigTypeSelector } from '../../../../../redux/selectors/builder';
import {
  ICohortRow,
  IRowMeta,
  RowType,
  setCohortRowSubject,
} from '../../../../../redux/slices/oldBuilder';
import { subjectTooltipOption } from '../../../../KubitTooltip/globalTooltips';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import {
  QueryType,
  SimpleCohortTypeKey,
} from '../../../../../utils/apollo/graphqlSchema';
import { SUBJECT_EVENT_KEY } from '../../../../Chart/constants/utils';
import { assertNotNil } from '../../../../../utils/types';
import useQueryTypeFromLocation from '../../../../../utils/routes/useQueryTypeFromLocation';
import BuilderEnumSelect from '../../../BuilderFields/BuilderEnumSelect';
import useAnalysisBuilderInfo from '../../../../../redux/selectorHooks/useAnalysisBuilderInfo';

interface IProps {
  cohortRow: ICohortRow;
  rowMeta: IRowMeta;
}

export default ({ cohortRow, rowMeta }: IProps) => {
  const dispatch = useAppDispatch();

  const queryType = useQueryTypeFromLocation();

  const { analysisId, simpleCohortTypeKey, subject } = cohortRow;
  assertNotNil(subject);

  const cohortConfigSelector = useAppSelector((state) =>
    cohortConfigTypeSelector(state, simpleCohortTypeKey),
  );

  const analysisBuilderInfo = useAnalysisBuilderInfo(analysisId);
  const builderInfo = analysisBuilderInfo?.query;

  const options = useMemo(() => {
    const subjectList = cohortConfigSelector.subjects;

    // KB-6740
    // IMPORTANT: Restriction for Funnel cohort row in RowType.globalCohorts
    // Once funnel analysis has been selected:
    // 1. On Query page: subject list should be: [funnel subject, Event subject]
    // 2. In all other reports: [funnel subject]
    // by default, Event subject should never be an option to select!
    if (
      rowMeta.rowType === RowType.globalCohorts &&
      simpleCohortTypeKey === SimpleCohortTypeKey.Funnel &&
      analysisId &&
      builderInfo?.__typename === 'FunnelQuery' &&
      builderInfo.subject
    ) {
      const funnelSubject = builderInfo.subject.key;

      return subjectList.filter((subjectOption) => {
        // Case 1: Query page
        if (queryType === QueryType.Expression) {
          return (
            subjectOption.key === funnelSubject ||
            subjectOption.key === SUBJECT_EVENT_KEY
          );
        }

        // Case 2: all others
        return subjectOption.key === funnelSubject;
      });
    }

    return subjectList.filter(
      (subjectOption) => subjectOption.key !== SUBJECT_EVENT_KEY,
    );
  }, [
    analysisId,
    builderInfo,
    cohortConfigSelector,
    rowMeta,
    simpleCohortTypeKey,
    queryType,
  ]);

  const handleChange = (newSubject: string) => {
    dispatch(
      setCohortRowSubject({
        value: newSubject,
        rowMeta,
      }),
    );
  };

  return (
    <BuilderEnumSelect
      label={'Subject'}
      enumOptions={options}
      selectedOption={subject}
      onChange={handleChange}
      tooltipOption={subjectTooltipOption}
    />
  );
};
